import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AccountType} from '../../model';
import {parseDate} from '../../utils';
import {ApiService, HttpParams, IdentificationObservable, OptionalIdentification} from '../shared/api.service';
import {AppStateService} from '../shared/app-state.service';
import {AuthService} from '../shared/auth.service';

export interface PersonNameResponse {
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  suffix: string;
  altLangInfo: any[]; // TODO?
}

export interface PersonResponse extends PersonNameResponse {
  id: string;
}

export interface StaffInfoResponse extends PersonNameResponse {
  phone: string;
  agent: boolean;
}

interface SubmittalsResponse {
  staff: StaffInfoResponse[];
  submittals: SubmittalResponse[];
}

interface OrderResponse {
  orderId: string;
  isRequest: boolean;
  position: string;
  startDate: string;
  endDate: string;
  hiredDate: string;
  statusId: number;
  placementTypeId: number;
  skills: string[];
  placementLength: number;
  placementLengthTypeId: number;
  responsibilities: string;
  fitDetails: string;
  createDate: string;
  rateTypeId: number;
  rateMin: number;
  rateMax: number;
  salaryMin: number;
  salaryMax: number;
  currencySymbol: string;
  roleToFill: string;
  maxTrackerStatus: number;
  numUnreviewedCandidates: number;
  hasSubmittals: boolean;
  latestSubmittal: string;
  latestTalentSubmittalProfileId: string | null;
  hasCandidates: boolean;
  latestTrackerActivity: TrackerActivityResponse;
  talent: PersonResponse;
  icName: string | null;
  incompleteQccId: number | null;
  incompleteQccTime: string;
  lastReviewTime: string;
  unsignedTimecardCount: number;
  unsignedTimecardTime: string;
  lastTimecardSignedTime: string;
  talentPool: boolean;
  reviewed: boolean;
  macCreated: boolean;
  openings: number;
  placedCandidates: number;
  workingCandidates: number;
  parentOrderId: string;
  positionStatus: string;
  endOfAssignmentEventType: string | null;
  endOfAssignmentEventTime: string | null;
  endDateUpdatedTime: string | null;
  endOfAssignmentRequestedEndDate: string | null;
}

interface TrackerActivityResponse {
  typeId: number;
  createDate: string;
  activityDate: string;
  talentFirstName: string;
  talentLastName: string;
}

interface SubmittalResponse {
  id: string;
  date: string;
  orderId: string;
  position: string;
  statusId: number;
  rate: number;
  availability: string;
  profile: Talent.Profile;
  profileUpdated: boolean;
  talentBridgeSalary: string;
  personId: string;
  currencySymbol: string;
  workedWithMe: boolean;
  workingWithMe: boolean;
  workedOnOrder: boolean;
  workingOnOrder: boolean;
  availabilityTypeId: number;
  numWeeksNotice: number;
  country: string;
  dateAvailable: string;
  availabilityUpdated: string;
  trackerHistory: TrackerActivityResponse[];
  friendlyTitle: boolean;
  rating: number;
  ratingCount: number;
  feedback: Talent.TalentFeedback[];
  independentAssessment: Talent.Interview;
  skillAssessments: Talent.SkillAssessment[];
  ratings: Talent.InterviewRating[];
  reviewedSites: string[];
  codingChallenges: Talent.CodingChallenge[];
  submittalFeedbackList: SubmittalFeedbackBody[];
  rateHidden?: boolean;
}

interface OrdersResponse {
  orders: OrderResponse[];
  submittals: SubmittalResponse[];
}

export interface SaveQccReplyData {
  rating: number;
  notes: string;
  shareFeedback: boolean;
  companyRating?: number;
  companyNotes?: string;
  id?: number;
  order?: number;
}

export interface TimecardResponse {
  timecardId: number;
  submitted: string;
  talent: PersonResponse;
  reportTo: PersonResponse;
  weekStartingMonday: string;
  weekEndingSunday: string;
  weekStartingDate: string;
  weekEndingDate: string;
  totalHours: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  rateTypeId: number;
  statusId: number;
  signed: string;
  approved: string;
  marketId: number;
}

export interface InvoiceResponse {
  invoiceId: number;
  invoiceNumber: string;
  talent: PersonResponse;
  balance: number;
  totalBilled: number;
  paid: number;
  invoiceDate: Date;
  currency: string;
  icName: string;
}

export interface StatementResponse {
  invoices: InvoiceResponse[];
  totalBalance: number;
  currencySymbol: string;
}

interface TimecardDetailsResponse {
  timecardId: number;
  weekEndingSunday: string;
  weekStartingMonday: string;
  weekStartingDate: string;
  weekEndingDate: string;
  totalHours: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalDoubletimeHours: number;
  jobTitle: string;
  monTimeIn: string;
  monTimeOut: string;
  monTimeLunch: string;
  monTimeTotal: number;
  tueTimeIn: string;
  tueTimeOut: string;
  tueTimeLunch: string;
  tueTimeTotal: number;
  wedTimeIn: string;
  wedTimeOut: string;
  wedTimeLunch: string;
  wedTimeTotal: number;
  thuTimeIn: string;
  thuTimeOut: string;
  thuTimeLunch: string;
  thuTimeTotal: number;
  friTimeIn: string;
  friTimeOut: string;
  friTimeLunch: string;
  friTimeTotal: number;
  satTimeIn: string;
  satTimeOut: string;
  satTimeLunch: string;
  satTimeTotal: number;
  sunTimeIn: string;
  sunTimeOut: string;
  sunTimeLunch: string;
  sunTimeTotal: number;
  submitted: string;
  reportsTo: PersonResponse;
  talent: PersonResponse;
  statusId: number;
  statusName: string;
  signed: string;
  signedBy: PersonResponse;
  approved: string;
  approvedBy: PersonResponse;
  rateTypeId: number;
  useDtr: boolean;
  timeIntervalList: TimeIntervalResponse[];
  marketId: number;
}

interface TimeIntervalResponse {
  timeIntervalId: number;
  startTime: string;
  endTime: string;
  totalHours: number;
  projectCode: string;
  accountingCode: string;
}

interface QccReplyResponse {
  id: number;
  order: number;
  rating: number;
  notes: string;
  talent: PersonNameResponse;
  icName: string;
  shareFeedback: boolean;
  date: string;
}

// As opposed to full File interface
interface BlobWithName extends Blob {
  name?: string;
}

export interface QccInfoResponse {
  token: string;
  orders: QccResponse[];
  companyRating: number;
  companyNotes: string;
  brand: string;
  locale: string;
}

export interface QccResponse {
  id: number;
  order: number;
  rating: number;
  notes: string;
  talent: PersonNameResponse;
  icName: string;
  shareFeedback: boolean;
}

export interface SaveQccData {
  replies: SaveQccReplyData[];
  companyNotes: string;
  companyRating: number;
}

export interface DiversityMetrics {
  applied: DiversityMetricsItem;
  submitted: DiversityMetricsItem;
  interviewed: DiversityMetricsItem;
  offerExtended: DiversityMetricsItem;
  accepted: DiversityMetricsItem;
  rejected: DiversityMetricsItem;

  orderCount: number;
  talentCount: number;
}

export interface DiversityMetricsSegmented {
  submitted: DiversityMetricsItem;
  interviewed: DiversityMetricsItem;
  hired: DiversityMetricsItem;
  year: number;
  quarter: number;

  orderCount: number;
  talentCount: number;

  quarterOrderCount: number;
}

export interface DiversityMetricsItem {
  orderCount: number;
  talentCount: number;
  stageCount: number;

  genderNotSet: number;
  genderFemale: number;
  genderMale: number;
  genderNotDisclosed: number;
  genderNonBinary: number;
  genderUnknown: number;

  lgbtqMember: number;
  lgbtqNonMember: number;
  lgbtqNotDisclosed: number;
  lgbtqUnknown: number;

  ethnicityNotDisclosed: number;
  ethnicityWhite: number;
  ethnicityBlackOrAfricanAmerican: number;
  ethnicityHispanicOrLatino: number;
  ethnicityAsian: number;
  ethnicityAmericanIndianOrAlaskanNative: number;
  ethnicityNativeHawaiianOrOtherPacificIslander: number;
  ethnicityTwoOrMoreRaces: number;
  ethnicityUnknown: number;

  neurodivergentYes: number;
  neurodivergentNo: number;
  neurodivergentNotDisclosed: number;
  neurodivergentUnknown: number;

  veteranVietnam: number;
  veteranSpecialDisabled: number;
  veteranOtherProtected: number;
  veteranNewlySeparated: number;
  veteranNotAVeteran: number;
  veteranArmedForcesServiceMedal: number;
  veteranNotDisclosed: number;
  veteranIsVeteran: number;

  disabledYes: number;
  disabledNo: number;
  disabledNotDisclosed: number;
  disabledUnknown: number;
}


@Injectable()
export class ClientApiService {

  constructor(private Api: ApiService,
              private Auth: AuthService,
              private AppState: AppStateService) {
  }

  sendEmail(message: string, orderId?: string, email?: string): IdentificationObservable<void> {
    let data = new HttpParams().set('message', message);
    if (orderId) data = data.set('orderId', orderId);
    if (email) data = data.set('email', email);
    return this.Api.httpWrapper<OptionalIdentification<void>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/email`,
      data,
      authenticated: true
    });
  }

  getOrder(orderId: string): Observable<Order> {
    return this.Api.httpWrapper<OrderResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}`,
      authenticated: true
    }).pipe(map(order => this.transformOrder(order)));
  }

  getChildOrders(orderId: string): Observable<Order[]> {
    return this.Api.httpWrapper<OrderResponse[]>({
      method: 'GET',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/children`,
      authenticated: true
    }).pipe(map(childOrders => childOrders.map(order => this.transformOrder(order))));
  }

  getSubmittals(orderId: string): Observable<Submittals> {
    return this.Api.httpWrapper<SubmittalsResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/submittals`,
      // cache: this.cache,
      authenticated: true
    }).pipe(map((data): Submittals => ({
      staff: data.staff,
      submittals: this.transformSubmittals(data.submittals)
    })));
  }

  getFavorites(): Observable<string[]> {
    const user = this.Auth.user$.getValue();
    if (this.Auth.isFullyAuthenticated() &&
      user && user.accountTypeId !== AccountType.TimecardOnly &&
      !this.Auth.masquerading$.getValue()) {
      return this.Api.httpWrapper<string[]>({
        method: 'GET',
        url: `${environment.apiHost}/v1/pools/favorites`,
        authenticated: true
      });
    } else {
      return of([]);
    }
  }

  addFavorite(id: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/pools/favorite/${id}`,
      authenticated: true
    });
  }

  removeFavorite(id: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'DELETE',
      url: `${environment.apiHost}/v1/pools/favorite/${id}`,
      authenticated: true
    });
  }

  saveTitle(orderId: string, title: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/title`,
      data: title,
      authenticated: true
    });
  }

  reviewCandidate(profileId: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/candidate/${profileId}/review`,
      authenticated: true
    });
  }

  scheduleCandidateInterview(orderId: string, profileId: string, message: string, email?: string): IdentificationObservable<number> {
    let data = new HttpParams().set('message', message);
    if (email) data = data.set('email', email);
    return this.Api.httpWrapper<OptionalIdentification<number>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/candidate/${profileId}/interview`,
      data,
      authenticated: true
    });
  }

  hireCandidate(orderId: string, profileId: string, message: string, email?: string): IdentificationObservable<number> {
    let data = new HttpParams().set('message', message);
    if (email) data = data.set('email', email);
    return this.Api.httpWrapper<OptionalIdentification<number>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/candidate/${profileId}/hire`,
      data,
      authenticated: true
    });
  }

  reconsiderCandidate(orderId: string, profileId: string, message: string, email?: string): IdentificationObservable<number> {
    let data = new HttpParams().set('message', message);
    if (email) data = data.set('email', email);
    return this.Api.httpWrapper<OptionalIdentification<number>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/candidate/${profileId}/reconsider`,
      data,
      authenticated: true
    });
  }

  passCandidate(orderId: string, profileId: string, message: string, email?: string, reason?: string): IdentificationObservable<number> {
    let data = new HttpParams().set('message', message);
    if (email) data = data.set('email', email);
    if (reason) data = data.set('reason', reason);
    return this.Api.httpWrapper<OptionalIdentification<number>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/candidate/${profileId}/pass`,
      data,
      authenticated: true
    });
  }

  cancelCandidate(orderId: string, profileId: string, message: string, email?: string, reason?: string): IdentificationObservable<number> {
    let data = new HttpParams().set('message', message);
    if (email) data = data.set('email', email);
    if (reason) data = data.set('reason', reason);
    return this.Api.httpWrapper<OptionalIdentification<number>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/candidate/${profileId}/cancel`,
      data,
      authenticated: true
    });
  }

  getNotes(orderId: string): Observable<Note[]> {
    return this.Api.httpWrapper<Note[]>({
      method: 'GET',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/notes`,
      authenticated: true
    });
  }

  addNote(orderId: string, text: string, data: any): Observable<number> {
    let params = new HttpParams().set('text', text);
    if (data) params = params.set('data', JSON.stringify(data));
    return this.Api.httpWrapper<number>({
      method: 'PUT',
      url: `${environment.apiHost}/v1/toolkit/order/${orderId}/notes`,
      params,
      authenticated: true
    });
  }

  deleteNote(orderId: string, noteId: number): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'DELETE',
      url: `${environment.apiHost}/v1/toolkit/notes/${noteId}`,
      authenticated: true
    });
  }

  saveQccReply(id: number, data: SaveQccReplyData): Observable<number> {
    return this.Api.httpWrapper<number>({
      method: 'POST',
      url: `${environment.apiHost}/v1/positions/qcc/${id}`,
      data,
      authenticated: true
    });
  }

  saveQccReplies(token: string, data: SaveQccData, notification?: boolean, verified?: boolean): Observable<number[]> {
    let params = new HttpParams();
    if (notification != null) params = params.set('notification', notification.toString());
    if (verified != null) params = params.set('verified', verified.toString());

    return this.Api.httpWrapper<number[]>({
      method: 'POST',
      url: `${environment.apiHost}/v1/qcc/${token}`,
      params,
      data
    });
  }

  getQccReviewerAccountInfo(orderId: number): Observable<User> {
    return this.Api.httpWrapper<User>({
      method: 'GET',
      url: `${environment.apiHost}/v1/qcc/order/${orderId}/reviewer`
    });
  }

  getPositions(): Observable<Order[]> {
    return this.Api.httpWrapper<OrderResponse[]>({
      method: 'GET',
      url: `${environment.apiHost}/v1/positions`,
      authenticated: true
    }).pipe(map(orders => this.transformOrders(orders)));
  }

  getUnsignedTimecards(): Observable<Timecard[]> {
    return this.Api.httpWrapper<TimecardResponse[]>({
      method: 'GET',
      url: `${environment.apiHost}/v1/timecards/unsigned`,
      authenticated: true
    }).pipe(map(timecards => this.transformTimecards(timecards)));
  }

  getTimecard(id: number): Observable<TimecardDetails> {
    return this.Api.httpWrapper<TimecardDetailsResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/timecards/${id}`,
      authenticated: true
    }).pipe(map(v => this.transformTimecardDetails(v)));
  }

  getTimecardStatusDetails(id: number): Observable<TimecardDetails> {
    return this.Api.httpWrapper<TimecardDetailsResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/timecards/${id}/status`,
      authenticated: true
    }).pipe(map(v => this.transformTimecardDetails(v)));
  }

  signTimecards(ids: number[]): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/timecards/signTimecards`,
      data: ids,
      authenticated: true
    });
  }

  // This also updates the interval codes for DTR timecards
  signSingleTimecard(timecard: TimecardDetails): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/timecards/signSingleTimecard`,
      data: timecard,
      authenticated: true
    });
  }

  disputeTimecards(disputeData: TimecardDisputeData): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/timecards/dispute`,
      data: disputeData,
      authenticated: true
    });
  }

  // This also updates the interval codes for DTR timecards
  disputeSingleTimecard(disputeData: TimecardDisputeSingleData): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/timecards/disputeSingle`,
      data: disputeData,
      authenticated: true
    });
  }

  getTimecards(position?: string) {
    let params = new HttpParams();
    if (position) params = params.set('position', position);
    return this.Api.httpWrapper<TimecardResponse[]>({
      method: 'GET',
      url: `${environment.apiHost}/v1/timecards`,
      params,
      authenticated: true
    }).pipe(map(timecards => this.transformTimecards(timecards)));
  }

  getStatement(): Observable<{invoices: Invoice[], totalBalance: number, currency: string}> {
    return this.Api.httpWrapper<StatementResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/invoices/statement`,
      authenticated: true
    }).pipe(map(data => ({
      invoices: this.transformInvoices(data.invoices),
      totalBalance: data.totalBalance,
      currency: data.currencySymbol
    })));
  }

  getPools(): Observable<Orders> {
    return this.Api.httpWrapper<OrdersResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/pools`,
      authenticated: true
    }).pipe(map(data => ({
      orders: this.transformOrders(data.orders),
      submittals: this.transformSubmittals(data.submittals)
    })));
  }

  getPool(position?: string): Observable<Orders> {
    return this.Api.httpWrapper<OrdersResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/pools/${position}`,
      authenticated: true
    }).pipe(map(data => ({
      orders: this.transformOrders(data.orders),
      submittals: this.transformSubmittals(data.submittals)
    })));
  }

  getTimecardPdf(id: number): Observable<Blob> {
    return this.Api.httpWrapper<Blob>({
      method: 'GET',
      url: `${environment.apiHost}/v1/timecards/${id}.pdf`,
      authenticated: true,
      responseType: 'blob'
    });
  }

  getInvoicePdf(id: number): Observable<Blob> {
    return this.Api.httpWrapper<Blob>({
      method: 'GET',
      url: `${environment.apiHost}/v1/invoices/${id}.pdf`,
      authenticated: true,
      responseType: 'blob'
    });
  }

  reviewPool(id: string): Observable<void> {
    if (!this.Auth.isFullyAuthenticated() || this.Auth.masquerading$.getValue()) {
      return of(null);
    }
    return this.Api.httpWrapper({
      method: 'POST',
      url: `${environment.apiHost}/v1/pools/${id}/review`,
      authenticated: true
    });
  }

  getPositionReviews(id: string): Observable<QccReply[]> {
    return this.Api.httpWrapper<QccReplyResponse[]>({
      method: 'GET',
      url: `${environment.apiHost}/v1/positions/${id}/reviews`,
      authenticated: true
    }).pipe(map(reviews => this.transformQccReplies(reviews)));
  }

  createEloquaLead(): Observable<void> {
    const user = this.Auth.user$.getValue();
    const market = user.markets[0];
    const brand = this.AppState.brand$.getValue();
    const prod = environment.name === 'prod';
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: 'https://s386085125.t.eloqua.com/e/f2',
      data: new HttpParams()
        .set('elqFormName', 'leadFormNative-1455548079501-636437514975051749')
        .set('elqSiteID', '386085125')
        .set('uTMSource1', 'myaquent')
        .set('uTMMedium1', 'display')
        .set('uTMCampaign1', 'AQ_NA_sig19')
        .set('uTMContent1', 'mac_hustle_mug')
        .set('uRL1', location.href)
        .set('lDFormID1', 'aq-slg-012219')
        .set('lDStatus1', 'Contact')
        .set('lDComments1', '*NOTE: This person downloaded the 2019 Salary Guide.')
        .set('lDLocationID1', market ? market.toString() : '')
        .set('lDPhone1', prod ? user.phone : '')
        .set('lDPriority1', '3')
        .set('title', user.jobTitle)
        .set('company', user.company)
        .set('firstName', user.firstName)
        .set('lastName', user.lastName)
        .set('emailAddress', prod ? user.email : 'myaquenttesting@aquent.com')
        .set('country', user.country)
        .set('stateProv', user.stateProv)
        .set('zipPostal', user.zipPostal)
    });
  }

  getReport(params: {reportType: string, fromDate: string, toDate: string, emailCopy: boolean}): Observable<BlobWithName> {
    return this.Api.httpWrapper<HttpResponse<Blob>>({
      method: 'GET',
      url: `${environment.apiHost}/v1/reports/${params.reportType}.xlsx`,
      authenticated: true,
      responseType: 'blob',
      params: new HttpParams()
        .set('startDate', params.fromDate)
        .set('endDate', params.toDate)
        .set('emailCopy', params.emailCopy.toString()),
      fullResponse: true
    }).pipe(map(res => {
      if (204 === res.status) {
        // HTTP_RESPONSE_NO_DATA
        return null;
      }
      const blob: BlobWithName = res.body;
      const headers = res.headers.getAll('Content-Disposition');
      const match = /filename="?([^";]+)"?/.exec(headers && headers[0]);
      blob.name = (match && match[1]) || 'report.xlsx';
      return blob;
    }));
  }

  requestTalent(talentRequest: TalentRequest): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/positions`,
      data: talentRequest,
      authenticated: true
    });
  }

  getQccInfo(emailTrackingId: string): Observable<QccInfoResponse> {
    return this.Api.httpWrapper<QccInfoResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/qcc/${emailTrackingId}`
    });
  }

  updatePositionEndDate(orderId: string, endDate: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/positions/${orderId}/eos/update`,
      data: {endDate},
      authenticated: true
    });
  }

  deferPositionEndDate(orderId: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/positions/${orderId}/eos/defer`,
      authenticated: true
    });
  }

  dismissPositionEndDate(orderId: string): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/positions/${orderId}/eos/dismiss`,
      authenticated: true
    });
  }

  readFeature(featureName: string): Observable<boolean> {
    return this.Api.httpWrapper<boolean>({
      method: 'GET',
      url: `${environment.apiHost}/v1/feature/${featureName}`,
      authenticated: true
    });
  }

  readDiversityMetricsByYearAndQuarter(): Observable<DiversityMetricsSegmented[]> {
    return this.Api.httpWrapper<DiversityMetricsSegmented[]>({
      method: 'POST',
      url: `${environment.apiHost}/v1/diversity/metrics-by-quarter`,
      authenticated: true
    });
  }

  markNewContactAsRegistered(contactId: number): Observable<void> {
    return this.Api.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/register/${contactId}`,
      authenticated: true
    });
  }

  private transformOrder(data: OrderResponse | null): Order | null {
    return data ? {
      orderId: data.orderId,
      isRequest: data.isRequest,
      position: data.position,
      startDate: parseDate(data.startDate),
      endDate: parseDate(data.endDate),
      hiredDate: parseDate(data.hiredDate),
      statusId: data.statusId,
      placementTypeId: data.placementTypeId,
      skills: data.skills,
      placementLength: data.placementLength,
      placementLengthTypeId: data.placementLengthTypeId,
      responsibilities: data.responsibilities,
      fitDetails: data.fitDetails,
      createDate: parseDate(data.createDate),
      rateTypeId: data.rateTypeId,
      rateMin: data.rateMin,
      rateMax: data.rateMax,
      salaryMin: data.salaryMin,
      salaryMax: data.salaryMax,
      currencySymbol: data.currencySymbol,
      roleToFill: data.roleToFill,
      maxTrackerStatus: data.maxTrackerStatus,
      numUnreviewedCandidates: data.numUnreviewedCandidates,
      hasSubmittals: data.hasSubmittals,
      latestSubmittal: parseDate(data.latestSubmittal),
      latestTalentSubmittalProfileId: data.latestTalentSubmittalProfileId,
      hasCandidates: data.hasCandidates,
      latestTrackerActivity: this.transformActivity(data.latestTrackerActivity),
      talent: data.talent,
      icName: data.icName,
      incompleteQccId: data.incompleteQccId,
      incompleteQccTime: parseDate(data.incompleteQccTime),
      lastReviewTime: parseDate(data.lastReviewTime),
      unsignedTimecardCount: data.unsignedTimecardCount,
      unsignedTimecardTime: parseDate(data.unsignedTimecardTime),
      lastTimecardSignedTime: parseDate(data.lastTimecardSignedTime),
      talentPool: data.talentPool,
      reviewed: data.reviewed,
      macCreated: data.macCreated,
      openings: data.openings,
      placedCandidates: data.placedCandidates,
      workingCandidates: data.workingCandidates,
      parentOrderId: data.parentOrderId,
      positionStatus: data.positionStatus,
      endOfAssignmentEventType: data.endOfAssignmentEventType,
      endOfAssignmentEventTime: parseDate(data.endOfAssignmentEventTime),
      endDateUpdatedTime: parseDate(data.endDateUpdatedTime),
      endOfAssignmentRequestedEndDate: parseDate(data.endOfAssignmentRequestedEndDate)
    } : null;
  }

  private transformOrders(orders: OrderResponse[] | null): Order[] {
    return orders ? orders.map(data => this.transformOrder(data)) : [];
  }

  private transformActivity(data: TrackerActivityResponse | null): TrackerActivity | null {
    return data ? {
      typeId: data.typeId,
      createDate: parseDate(data.createDate),
      activityDate: parseDate(data.activityDate),
      talentFirstName: data.talentFirstName,
      talentLastName: data.talentLastName
    } : null;
  }

  private transformActivities(activities: TrackerActivityResponse[] | null): TrackerActivity[] {
    return activities ? activities.map(data => this.transformActivity(data)) : [];
  }

  private transformSubmittals(submittals: SubmittalResponse[] | null): Submittal[] {
    return submittals ? submittals.map(data => ({
      id: data.id,
      date: parseDate(data.date),
      orderId: data.orderId,
      position: data.position,
      statusId: data.statusId,
      rate: data.rate,
      availability: data.availability,
      profile: data.profile ? {
        ...data.profile,
        // Replace title with position title when working + friendly title is available
        ...(!data.profileUpdated && data.friendlyTitle && (data.workingOnOrder || data.workedOnOrder) ? {
          title: data.position
        } : {})
      } : null,
      talentBridgeSalary: data.talentBridgeSalary,
      personId: data.personId,
      currencySymbol: data.currencySymbol,
      workedWithMe: data.workedWithMe,
      workingWithMe: data.workingWithMe,
      workedOnOrder: data.workedOnOrder,
      workingOnOrder: data.workingOnOrder,
      availabilityTypeId: data.availabilityTypeId,
      numWeeksNotice: data.numWeeksNotice,
      country: data.country,
      dateAvailable: parseDate(data.dateAvailable),
      availabilityUpdated: parseDate(data.availabilityUpdated),
      trackerHistory: this.transformActivities(data.trackerHistory),
      friendlyTitle: data.friendlyTitle,
      rating: data.rating,
      ratingCount: data.ratingCount,
      feedback: data.feedback,
      independentAssessment: data.independentAssessment,
      codingChallenges: data.codingChallenges,
      skillAssessments: data.skillAssessments,
      ratings: data.ratings,
      reviewedSites: data.reviewedSites,
      submittalFeedbackList: data.submittalFeedbackList || [],
      rateHidden: data.rateHidden
    })) : [];
  }

  private transformTimecards(timecards: TimecardResponse[]): Timecard[] {
    return timecards ? timecards.map(data => ({
      timecardId: data.timecardId,
      submitted: parseDate(data.submitted),
      talent: data.talent,
      reportTo: data.reportTo,
      weekStartingMonday: parseDate(data.weekStartingMonday),
      weekEndingSunday: parseDate(data.weekEndingSunday),
      weekStartingDate: parseDate(data.weekStartingDate),
      weekEndingDate: parseDate(data.weekEndingDate),
      totalHours: data.totalHours,
      totalRegularHours: data.totalRegularHours,
      totalOvertimeHours: data.totalOvertimeHours,
      rateTypeId: data.rateTypeId,
      statusId: data.statusId,
      signed: parseDate(data.signed),
      approved: parseDate(data.approved),
      marketId: data.marketId
    })) : [];
  }

  private transformTimecardDetails(data: TimecardDetailsResponse): TimecardDetails {
    return {
      timecardId: data.timecardId,
      weekEndingSunday: parseDate(data.weekEndingSunday),
      weekStartingMonday: parseDate(data.weekStartingMonday),
      weekStartingDate: parseDate(data.weekStartingDate),
      weekEndingDate: parseDate(data.weekEndingDate),
      totalHours: data.totalHours,
      totalRegularHours: data.totalRegularHours,
      totalOvertimeHours: data.totalOvertimeHours,
      totalDoubletimeHours: data.totalDoubletimeHours,
      jobTitle: data.jobTitle,
      monTimeIn: data.monTimeIn,
      monTimeOut: data.monTimeOut,
      monTimeLunch: data.monTimeLunch,
      monTimeTotal: data.monTimeTotal,
      tueTimeIn: data.tueTimeIn,
      tueTimeOut: data.tueTimeOut,
      tueTimeLunch: data.tueTimeLunch,
      tueTimeTotal: data.tueTimeTotal,
      wedTimeIn: data.wedTimeIn,
      wedTimeOut: data.wedTimeOut,
      wedTimeLunch: data.wedTimeLunch,
      wedTimeTotal: data.wedTimeTotal,
      thuTimeIn: data.thuTimeIn,
      thuTimeOut: data.thuTimeOut,
      thuTimeLunch: data.thuTimeLunch,
      thuTimeTotal: data.thuTimeTotal,
      friTimeIn: data.friTimeIn,
      friTimeOut: data.friTimeOut,
      friTimeLunch: data.friTimeLunch,
      friTimeTotal: data.friTimeTotal,
      satTimeIn: data.satTimeIn,
      satTimeOut: data.satTimeOut,
      satTimeLunch: data.satTimeLunch,
      satTimeTotal: data.satTimeTotal,
      sunTimeIn: data.sunTimeIn,
      sunTimeOut: data.sunTimeOut,
      sunTimeLunch: data.sunTimeLunch,
      sunTimeTotal: data.sunTimeTotal,
      submitted: parseDate(data.submitted),
      reportsTo: data.reportsTo,
      talent: data.talent,
      statusId: data.statusId,
      statusName: data.statusName,
      signed: parseDate(data.signed),
      signedBy: data.signedBy,
      approved: parseDate(data.approved),
      approvedBy: data.approvedBy,
      rateTypeId: data.rateTypeId,
      useDtr: data.useDtr,
      timeIntervalList: this.transformIntervals(data.timeIntervalList),
      marketId: data.marketId
    };
  }

  private transformIntervals(data: TimeIntervalResponse[]): TimeInterval[] {
    return data ? data.map(v => ({
      timeIntervalId: v.timeIntervalId,
      startTime: parseDate(v.startTime),
      endTime: parseDate(v.endTime),
      totalHours: v.totalHours,
      projectCode: v.projectCode,
      accountingCode: v.accountingCode
    })) : [];
  }

  private transformInvoices(invoices: InvoiceResponse[]): Invoice[] {
    return invoices ? invoices.map(data => ({
      invoiceId: data.invoiceId,
      number: data.invoiceNumber,
      talent: data.talent,
      invoiceDate: data.invoiceDate,
      paid: data.paid,
      billed: data.totalBilled,
      balance: data.balance,
      currency: data.currency,
      icName: data.icName
    })) : [];
  }

  private transformQccReplies(replies: QccReplyResponse[]): QccReply[] {
    return replies ? replies.map(data => ({
      id: data.id,
      order: data.order,
      rating: data.rating,
      notes: data.notes,
      talent: data.talent,
      icName: data.icName,
      shareFeedback: data.shareFeedback,
      date: parseDate(data.date)
    })) : [];
  }
}
